import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEdit } from "react-icons/fa";
import { MdAddCircleOutline, MdOutlineDeleteForever, MdOutlineFaceRetouchingOff } from "react-icons/md";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import Select from "react-select";

import { ReactComponent as BluePenIcon } from "../../../assets/blue_pen_icon.svg";
import { ReactComponent as CertificateIcon } from "../../../assets/cerificate_icon.svg";
import { ReactComponent as GreenCheckIcon } from "../../../assets/green_check_icon.svg";
import { ReactComponent as StudentsIcon } from "../../../assets/Group320.svg";
import { ReactComponent as ClassroomIcon } from "../../../assets/Group322.svg";
import { ReactComponent as AssignIcon } from "../../../assets/Group453.svg";
import { ReactComponent as OpenBookIcon } from "../../../assets/open_book_icon.svg";
import { ReactComponent as RedCloseIcon } from "../../../assets/red_close_icon.svg";
import stars_icon from "../../../assets/stars_transparent.png";
import { ReactComponent as ClassroomReportsIcon } from "../../../assets/Subtraction3.svg";
import teacher_placeholder from "../../../assets/teacher_placeholder.svg";
import Button from "../../../components/Button";
import CustomDataTable from "../../../components/CustomDataTable";
import LoadingSpinner from "../../../components/LoadingSpinner";
import AddClassroomModal from "../../../components/modals/common/AddClassroomModal";
import DeactivateTeacherAccountModal from "../../../components/modals/school/DeactivateTeacherAccountModal";
import DeleteTeacherAccountModal from "../../../components/modals/school/DeleteTeacherAccountModal";
import EditTeacherModal from "../../../components/modals/school/EditTeacherModal";
import ReassignModal from "../../../components/modals/school/ReassignModal";
import { USER_TYPE } from "../../../constants/common";
import { useTheme } from "../../../context/ThemeContext";
import { resetBreadcrumbs, setBreadcrumbDynamicPathNames } from "../../../redux/slices/breadcrumbsSlice";
import classroomServices from "../../../services/classroomServices";
import schoolServices from "../../../services/schoolServices";
import teacherServices from "../../../services/teacherServices";
import { selectStylesV1 } from "../../../styles/selectStyles";
import { getLanguage } from "../../../utils/getLanguage";
import { useNavigateWithLang } from "../../../utils/navigation";

const SchoolTeacherDetails = () => {
  const { id: teacherID } = useParams();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const dispatch = useDispatch();
  const lang = getLanguage();
  const navigate = useNavigateWithLang();

  const [selectedClassroom, setSelectedClassroom] = useState(null);
  const [teacher, setTeacher] = useState(null);
  const [teacherCourses, setTeacherCourses] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingClassrooms, setLoadingClassrooms] = useState(false);

  const [classrooms, setClassrooms] = useState([]);
  const [classroomDetails, setClassroomDetails] = useState(null);
  const [classroomTopStudents, setClassroomTopStudents] = useState([]);
  const [projectStats, setProjectStats] = useState(null);
  const [classroomStats, setClassroomStats] = useState("");
  const [schoolTopTenStudentsByClassroom, setSchoolTopTenStudentsByClassroom] = useState(null);

  const addClassroomModalRef = useRef();
  const deactivateTeacherModalRef = useRef();
  const deleteTeacherModalRef = useRef();
  const reassignModalRef = useRef();
  const editTeacherModalRef = useRef();

  const openAddClassroomModal = (teacherID) => addClassroomModalRef.current.openModal(teacherID);
  const openDeactivateTeacherModal = (teacher) => deactivateTeacherModalRef.current.openModal(teacher);
  const openDeleteTeacherModal = (teacher) => deleteTeacherModalRef.current.openModal(teacher);
  const openReassignModal = (classroom) => reassignModalRef.current.openModal(classroom);
  const openEditTeacherModal = (teacher) => editTeacherModalRef.current.openModal(teacher);

  const handleSelect = async (option) => {
    setSelectedClassroom(option);
    const response = await classroomServices.getClassroomDetails(option.value);
    setClassroomDetails(response);
    fetchClassroomTopStudents(response?.classroom?.id);
    getClassroomProjectStats(response?.classroom?.id);
    fetchSchoolTopTenStudentsByClassroom(response?.classroom?.id);
  };

  const handleAddClassroomSuccess = () => {
    getTeacherClassrooms();
  };

  const handleDeactivateTeacherSuccess = () => {
    navigate(`/${USER_TYPE.SCHOOL.ROLE}/teachers`);
  };

  const handleDeleteTeacherSuccess = () => {
    navigate(`/${USER_TYPE.SCHOOL.ROLE}/teachers`);
  };

  const handleReassign = () => {
    console.log("Reassignment complete");
  };

  const handleEditTeacherSuccess = () => {
    getTeacherDetails();
  };

  const navigateStudentDetails = (id) => {
    navigate(`/${USER_TYPE.SCHOOL.ROLE}/students/${id}`);
  };

  const getTeacherDetails = async () => {
    try {
      const response = await teacherServices.getTeacherByID(teacherID);

      setTeacher(response.teacher);
      setTeacherCourses(response.courses);

      dispatch(setBreadcrumbDynamicPathNames([response.teacher.name]));

      if (response.teacher.is_disabled) {
        navigate(`/${USER_TYPE.SCHOOL.ROLE}/teachers`);
      }
    } catch (error) {
      console.error("Failed to fetch getTeacherByID:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClassroomTopStudents = async (classroomId) => {
    try {
      const response = await classroomServices.getClassroomTopStudents(classroomId);

      setClassroomTopStudents(response);
    } catch (error) {
      console.error("Failed to fetch:", error);
    } finally {
    }
  };

  const getTeacherClassrooms = async () => {
    setLoadingClassrooms(true);
    try {
      const response = await teacherServices.getClassroomsByTeacher(teacherID);

      const formattedClassrooms = response.data.map((classroom) => ({
        value: classroom.id,
        label: classroom.name,
      }));

      setClassrooms(formattedClassrooms);
    } catch (error) {
      console.error("Failed to fetch getClassroomsByTeacher:", error);
    } finally {
      setLoadingClassrooms(false);
    }
  };

  const getClassroomProjectStats = async (classroomID) => {
    try {
      const response = await classroomServices.classroomProjectsStats(classroomID);
      const response2 = await classroomServices.getClassroomStats(classroomID);

      setProjectStats(response);
      setClassroomStats(response2);
    } catch (error) {
      console.error("Error fetching students:", error);
    } finally {
    }
  };

  const fetchSchoolTopTenStudentsByClassroom = async (classroomID) => {
    try {
      const response = await schoolServices.getSchoolTopTenStudentsByClassroom(classroomID);
      setSchoolTopTenStudentsByClassroom(response);
    } catch (error) {
      console.error("Error fetching students:", error);
    } finally {
    }
  };

  useEffect(() => {
    getTeacherDetails();
    getTeacherClassrooms();
  }, [lang]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    return () => {
      dispatch(resetBreadcrumbs());
    };
  }, []);

  const columns = [
    {
      name: t("name"),
      field: "student_name",
      sortable: true,
      body: (rowData) => (
        <Button
          tooltip={rowData.student_name}
          labelStyle="block w-80 overflow-hidden truncate whitespace-nowrap text-start"
          label={rowData.student_name}
          variant="link"
          color="blue"
          onClick={() => navigateStudentDetails(rowData.student_id)}
        />
      ),
    },
    {
      name: t("points"),
      field: "total_points",
      sortable: true,
    },
  ];

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-start lg:space-x-2">
        <Button
          iconLeft={<MdAddCircleOutline size={22} />}
          label={t("add_new_classroom")}
          color="green"
          onClick={() => openAddClassroomModal(teacherID)}
        />
        <Button
          iconLeft={<MdOutlineFaceRetouchingOff size={20} />}
          label={t("deactivate_account")}
          color="gray"
          onClick={() => openDeactivateTeacherModal(teacher)}
        />
        <Button
          iconLeft={<MdOutlineDeleteForever size={22} />}
          label={t("delete_account")}
          color="red"
          onClick={() => openDeleteTeacherModal(teacher)}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
        {/* Teacher Info Section */}
        <div className="col-span-12 rounded-xl bg-white p-4 shadow-md dark:bg-dark-surface xl:col-span-6">
          <div className="flex items-center justify-between">
            <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span className="font-semibold">{t("last_login")}:</span>
              {teacher?.last_login || t("not_available")}
            </div>
            <Button
              className="!px-0 hover:bg-transparent"
              icon={<FaEdit size={20} />}
              variant="text"
              onClick={() => openEditTeacherModal(teacher)}
            />
          </div>
          <div className="flex flex-col justify-between sm:flex-col md:flex-col lg:flex-row lg:items-center">
            <div className="mb-5 flex w-full flex-grow items-center gap-6 lg:mb-0">
              <img
                className="h-28 w-28 rounded-full border-4 border-blue-500 object-cover"
                src={teacher?.image || teacher_placeholder}
                alt="teacher"
              />
              <div className="max-w-full overflow-hidden">
                <h2 className="break-words text-xl font-semibold text-text dark:text-white">
                  {teacher?.name}
                </h2>
                <p className="break-words text-sm font-light text-mutedText dark:text-dark-mutedText">
                  {teacher?.username}
                </p>
                <p className="break-words text-sm font-light text-mutedText dark:text-dark-mutedText">
                  {teacher?.email}
                </p>
                <p className="break-words text-sm font-light text-mutedText dark:text-dark-mutedText">
                  {teacher?.phone_number}
                </p>
              </div>
            </div>
            <div className="mx-4 hidden h-40 border-l border-[#e6e6e6] dark:border-[#FAFBFC0D] lg:block"></div>
            <div className="w-full text-left lg:w-1/4">
              <p className="text-justify text-sm font-light text-mutedText dark:text-dark-mutedText">
                {t("courses")}:
              </p>
              <div className="relative flex flex-wrap gap-1 text-start">
                {teacherCourses?.slice(0, 3).map((course, index) => (
                  <span
                    key={index}
                    className="rounded-full text-xs font-medium text-mutedText dark:text-dark-mutedText"
                  >
                    {course.course}
                  </span>
                ))}
                {teacherCourses?.length > 3 && (
                  <div className="group relative">
                    <span className="cursor-pointer rounded-full bg-mutedText px-3 py-1 text-xs font-medium text-white dark:text-dark-mutedText">
                      +{teacherCourses.length - 3}
                    </span>
                    <div className="absolute left-0 top-7 hidden w-max rounded-lg bg-white p-2 shadow-md group-hover:block dark:bg-dark-surface">
                      {teacherCourses.slice(3).map((course, index) => (
                        <span
                          key={index}
                          className="mb-1 block rounded-md py-1 text-xs font-medium text-mutedText dark:text-dark-mutedText"
                        >
                          {course.course}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Classrooms and Students Summary */}
        <div className="col-span-12 flex flex-col items-center justify-around rounded-xl bg-white p-4 shadow-md dark:bg-dark-surface lg:col-span-6 xl:col-span-2">
          <div className="flex flex-col items-center justify-center">
            <ClassroomIcon className="mb-4 h-14 w-14 text-mutedText dark:text-dark-mutedText" />
            <h3 className="text-center font-semibold text-mutedText dark:text-dark-mutedText">
              {t("classrooms")}
            </h3>
          </div>
          <p className="text-center text-[38px] font-bold text-blue-500">{teacher?.classrooms_count}</p>
        </div>
        <div className="col-span-12 flex flex-col items-center justify-around rounded-xl bg-white p-4 shadow-md dark:bg-dark-surface lg:col-span-3 xl:col-span-2">
          <div className="flex flex-col items-center justify-center">
            <StudentsIcon className="mb-4 h-14 w-14 text-mutedText dark:text-dark-mutedText" />
            <h3 className="text-center font-semibold text-mutedText dark:text-dark-mutedText">
              {t("total_students")}
            </h3>
          </div>
          <p className="text-center text-[38px] font-bold text-blue-500">{teacher?.students_count}</p>
        </div>
        <div className="col-span-12 flex flex-col items-center overflow-hidden rounded-xl bg-white p-3 shadow-lg dark:bg-dark-surface lg:col-span-12 xl:col-span-2">
          <div className="flex flex-1 flex-col items-center justify-center px-4">
            <CertificateIcon className="mb-4 h-14 w-14 text-mutedText dark:text-dark-mutedText" />
            <h3 className="text-center font-semibold text-mutedText dark:text-dark-mutedText">
              {t("appreciation_certificate")}
            </h3>
          </div>
          <Button disabled variant="outlined" className="w-full" label={t("generate")} />
        </div>

        {/* Select Classroom, Challenges, and Playground Projects in One Row */}
        <div className="col-span-12 grid grid-cols-1 gap-4 md:grid-cols-12">
          <div className="col-span-12 flex w-full items-center rounded-xl bg-white p-4 shadow-md dark:bg-dark-surface xl:col-span-4">
            {/* React Select Autocomplete */}
            <Select
              noOptionsMessage={() => {
                return <p>{t("no_options")}</p>;
              }}
              loadingMessage={() => <p>{t("loading")}</p>}
              className="w-full"
              isClearable={true} // Allows clearing selection
              options={classrooms}
              value={selectedClassroom}
              isLoading={loadingClassrooms}
              onChange={handleSelect}
              placeholder={t("select_classroom")}
              styles={selectStylesV1(theme)} // Apply the styles with the theme
            />
          </div>

          {/* Display Selected Classroom */}
          {selectedClassroom && (
            <div className="col-span-12 flex items-center justify-between rounded-xl bg-white p-4 shadow-md dark:bg-dark-surface xl:col-span-8">
              <h3 className="text-lg font-semibold text-blue-500">
                {t("classroom_name")} - {selectedClassroom?.label}
              </h3>
              <p className="text-lg font-semibold text-mutedText dark:text-dark-mutedText">
                {t("students")} {classroomDetails?.student_count}
              </p>
              <Button
                variant="text"
                tooltip={t("reassign_classroom")}
                disabled={!selectedClassroom}
                onClick={() => openReassignModal(classroomDetails)}
                icon={
                  <AssignIcon
                    className={`${!selectedClassroom ? "text-mutedText dark:text-dark-mutedText" : "text-[#ff602b]"} `}
                  />
                }
              />
            </div>
          )}

          {selectedClassroom && (
            <div className="col-span-12 grid grid-cols-1 gap-4 md:grid-cols-12">
              {/* Classroom Report Section */}
              <div className="col-span-12 overflow-hidden rounded-xl bg-white shadow-lg dark:bg-dark-surface lg:col-span-6 xl:col-span-2">
                {/* Dark overlay */}
                <img
                  src={classroomDetails?.course?.image}
                  alt="Course"
                  className="h-36 w-full object-cover"
                />

                <div className="flex flex-col justify-end gap-1 bg-white p-4 dark:bg-dark-surface">
                  {/* Bottom content section */}
                  <h4 className="w-fit bg-[#842880] px-2 text-sm uppercase text-white dark:text-gray-300 rtl:w-20">
                    {t("course")}
                  </h4>
                  <h3 className="text-lg font-semibold text-[#842880] dark:text-white">
                    {classroomDetails?.course?.course}
                  </h3>
                </div>
              </div>
              {/* Classroom Report Section */}
              <div className="col-span-12 flex flex-col items-center justify-around rounded-xl bg-white p-4 shadow-md dark:bg-dark-surface lg:col-span-6 xl:col-span-2">
                <div className="flex flex-col items-center justify-center">
                  <OpenBookIcon className="mb-4 h-14 w-14 text-mutedText dark:text-dark-mutedText" />
                  <h3 className="text-center font-semibold text-mutedText dark:text-dark-mutedText">
                    {t("completed_lessons")}
                  </h3>
                </div>
                <div>
                  <p className="text-center text-[43px] font-semibold text-blue-500">
                    {classroomStats?.completed_lessons_count}
                  </p>
                  <span className="text-sm text-blue-500">
                    {t("out_of")} {classroomStats?.lesson_count}
                  </span>
                </div>
              </div>

              {/* Total Course Challenges / Projects */}
              <div
                className={`col-span-12 flex flex-col justify-center rounded-xl bg-white p-3 shadow-md dark:bg-dark-surface lg:col-span-6 ${classroomDetails?.course?.for_children ? "xl:col-span-6" : "xl:col-span-3"}`}
              >
                <div className="px-3">
                  <h3 className="font-semibold text-mutedText dark:text-dark-mutedText">
                    {classroomDetails?.course?.for_children
                      ? t("total_course_project")
                      : t("total_course_challenge")}
                  </h3>
                  <p className="mt-1 text-3xl font-bold text-gray-800 dark:text-white">
                    {classroomDetails?.course?.for_children
                      ? (projectStats?.approved_challenges || 0) +
                        (projectStats?.pending_challenges || 0) +
                        (projectStats?.rejected_challenges || 0)
                      : (projectStats?.approved_challenges || 0) +
                        (projectStats?.pending_challenges || 0) +
                        (projectStats?.rejected_challenges || 0)}
                  </p>
                  <div className="my-4">
                    <p className="mb-2 flex font-medium text-blue-600">
                      <span className="flex flex-1 items-center gap-2">
                        <BluePenIcon />
                        {t("pending")}
                      </span>
                      <span className="flex flex-1 justify-end font-medium">
                        {projectStats?.pending_challenges || 0}
                      </span>
                    </p>
                    <p className="mb-2 flex font-medium text-green-500">
                      <span className="flex-2 flex items-center gap-2">
                        <GreenCheckIcon />
                        {t("approved")}
                      </span>
                      <span className="flex flex-1 justify-end font-medium">
                        {projectStats?.approved_challenges || 0}
                      </span>
                    </p>
                    <p className="mb-2 flex font-medium text-red-500">
                      <span className="flex flex-1 items-center gap-2">
                        <RedCloseIcon />
                        {t("rejected")}
                      </span>
                      <span className="flex flex-1 justify-end font-medium">
                        {projectStats?.rejected_challenges || 0}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Playground Projects (only for non-children courses) */}
              {!classroomDetails?.course?.for_children && (
                <div className="col-span-12 flex flex-col justify-center rounded-xl bg-white p-3 shadow-md dark:bg-dark-surface lg:col-span-6 xl:col-span-3">
                  <div className="px-3">
                    <h3 className="font-semibold text-mutedText dark:text-dark-mutedText">
                      {t("playground_projects")}
                    </h3>
                    <p className="mt-1 text-3xl font-bold text-gray-800 dark:text-white">
                      {(projectStats?.approved_playground || 0) +
                        (projectStats?.pending_playground || 0) +
                        (projectStats?.rejected_playground || 0)}
                    </p>
                    <div className="my-4">
                      <p className="mb-2 flex font-medium text-blue-600">
                        <span className="flex flex-1 items-center gap-2">
                          <BluePenIcon />
                          {t("pending")}
                        </span>
                        <span className="flex flex-1 justify-end font-medium">
                          {projectStats?.pending_playground || 0}
                        </span>
                      </p>
                      <p className="mb-2 flex font-medium text-green-500">
                        <span className="flex-2 flex items-center gap-2">
                          <GreenCheckIcon />
                          {t("approved")}
                        </span>
                        <span className="flex flex-1 justify-end font-medium">
                          {projectStats?.approved_playground || 0}
                        </span>
                      </p>
                      <p className="mb-2 flex font-medium text-red-500">
                        <span className="flex flex-1 items-center gap-2">
                          <RedCloseIcon />
                          {t("rejected")}
                        </span>
                        <span className="flex flex-1 justify-end font-medium">
                          {projectStats?.rejected_playground || 0}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Classroom Report Section */}
              <div className="col-span-12 flex flex-col items-center overflow-hidden rounded-xl bg-white p-3 shadow-lg dark:bg-dark-surface lg:col-span-12 xl:col-span-2">
                <div className="flex flex-1 flex-col items-center justify-center px-4">
                  <ClassroomReportsIcon className="mb-4 h-14 w-14 text-blue-500" />
                  <h3 className="text-center font-semibold text-mutedText dark:text-dark-mutedText">
                    {t("classroom_report")}
                  </h3>
                </div>
                <Button
                  variant="outlined"
                  label={t("go_to_reports")}
                  onClick={() =>
                    navigate(`/${USER_TYPE.SCHOOL.ROLE}/classroom-reports?teacherId=${1}&classroomId=${2}`)
                  }
                />
              </div>
            </div>
          )}
        </div>
        {selectedClassroom && (
          <div className="col-span-12 grid grid-cols-1 gap-4 md:grid-cols-12">
            <div className="col-span-12 rounded-xl bg-white shadow-md dark:bg-dark-surface lg:col-span-9 xl:col-span-10">
              <CustomDataTable
                header={false}
                search={<h3 className="text-xl font-semibold text-green-500">{t("top_five_students")}</h3>}
                columns={columns}
                data={classroomTopStudents}
                withPagination={false}
                withSort
              />
            </div>
            <div className="col-span-12 flex flex-col items-center overflow-hidden rounded-xl bg-white p-3 shadow-lg dark:bg-dark-surface lg:col-span-12 xl:col-span-2">
              <img src={stars_icon} alt="icon" />

              {schoolTopTenStudentsByClassroom?.length > 0 ? (
                schoolTopTenStudentsByClassroom?.slice(0, 2).map((student, index) => (
                  <div
                    onClick={() => navigateStudentDetails(student?.id)}
                    key={student?.id || index}
                    className="group mb-2 w-full max-w-xs cursor-pointer rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 p-5 shadow-lg transition hover:scale-[1.02] dark:from-dark-surface dark:to-dark-surface/80"
                  >
                    <div className="flex flex-col items-center text-center">
                      <div className="relative mb-4">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white text-3xl font-bold text-blue-600 shadow-inner ring-4 ring-blue-300 dark:bg-gray-700 dark:ring-blue-500">
                          {student?.name?.charAt(0)}
                        </div>
                        <span className="absolute -bottom-2 right-0 rounded-full bg-yellow-400 px-1 py-1 text-xs font-semibold text-white shadow-md">
                          ⭐
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-blue-700 group-hover:text-blue-900 dark:text-blue-300">
                        {student?.name}
                      </h3>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-1 flex-col items-center justify-center px-4">
                  <p className="mb-4 text-center text-xs text-mutedText dark:text-dark-mutedText">
                    {t("no_students_from_classroom")}
                  </p>
                </div>
              )}

              <Button
                className="mt-2 w-full"
                variant="outlined"
                label={t("check_top_10_students")}
                onClick={() => navigate(`/${USER_TYPE.SCHOOL.ROLE}/top-10-students`)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <AddClassroomModal ref={addClassroomModalRef} onSubmit={handleAddClassroomSuccess} />
      <DeactivateTeacherAccountModal
        ref={deactivateTeacherModalRef}
        onSubmit={handleDeactivateTeacherSuccess}
      />
      <DeleteTeacherAccountModal ref={deleteTeacherModalRef} onSubmit={handleDeleteTeacherSuccess} />
      <ReassignModal ref={reassignModalRef} onSubmit={handleReassign} />
      <EditTeacherModal ref={editTeacherModalRef} onSubmit={handleEditTeacherSuccess} />
    </>
  );
};

export default SchoolTeacherDetails;
